import { useMemo } from 'react';
import { AlertTriangle, Wrench } from 'lucide-react';
import { InlineReasoning } from './inline-reasoning';
import { ToolCallRouter } from './tool-call-router';
import type { ThinkingStep } from './agent-chain-of-thought';
import { formatElapsed } from '../../utils/format';

interface ThoughtsPanelProps {
  steps: ThinkingStep[];
  elapsed: number;
  isRunning: boolean;
  error?: string;
}

// ─── Timeline entry types ──────────────────────────────────────────────────

type TimelineEntry =
  | { kind: 'reasoning'; key: string; text: string }
  | { kind: 'tool'; key: string; props: Record<string, unknown> };

function flattenSteps(steps: ThinkingStep[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  for (const step of steps) {
    for (let ri = 0; ri < step.reasoning.length; ri++) {
      entries.push({
        kind: 'reasoning',
        key: `r-${step.index}-${ri}`,
        text: String(step.reasoning[ri].text ?? ''),
      });
    }
    for (const tc of step.tools) {
      entries.push({
        kind: 'tool',
        key: `t-${String(tc.toolCallId ?? Math.random())}`,
        props: tc,
      });
    }
  }
  return entries;
}

// ─── Bullet components ─────────────────────────────────────────────────────

function ReasoningBullet({ isActive }: { isActive: boolean }) {
  return (
    <div
      className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border-2 ${
        isActive
          ? 'border-primary bg-primary animate-pulse'
          : 'border-muted-foreground/40 bg-background'
      }`}
    />
  );
}

function ToolBullet() {
  return (
    <div className="mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
      <Wrench className="h-3 w-3 text-muted-foreground/60" />
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────

export function ThoughtsPanel({ steps, elapsed, isRunning, error }: ThoughtsPanelProps) {
  const entries = useMemo(() => flattenSteps(steps), [steps]);
  const toolCount = useMemo(
    () => entries.filter((e) => e.kind === 'tool').length,
    [entries],
  );

  const headerLabel = error
    ? `Failed after ${formatElapsed(elapsed)}`
    : isRunning
      ? 'In progress'
      : `Completed in ${formatElapsed(elapsed)}`;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-4">
      <div className={`mb-3 text-xs ${error ? 'text-red-500' : 'text-muted-foreground'}`}>
        {headerLabel}
        {' · '}
        {steps.length} {steps.length === 1 ? 'step' : 'steps'}
        {toolCount > 0 && (
          <>
            {' · '}
            {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
          </>
        )}
      </div>

      <div className="space-y-0">
        {entries.map((entry, i) => {
          const isLast = i === entries.length - 1;
          const isActive = isRunning && isLast;

          return (
            <div key={entry.key} className="relative flex gap-3">
              {/* Vertical line + bullet — fixed width so content aligns */}
              <div className="flex w-3.5 flex-col items-center">
                {entry.kind === 'reasoning' ? (
                  <ReasoningBullet isActive={isActive} />
                ) : (
                  <ToolBullet />
                )}
                {!isLast && <div className="w-px flex-1 bg-border" />}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1 pb-4">
                {entry.kind === 'reasoning' ? (
                  <InlineReasoning text={entry.text} />
                ) : (
                  <ToolCallRouter {...entry.props} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mt-2 flex gap-2 rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
